import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

export const _frontmatter = {
  "title": "Disbursements",
  "includeToc": true,
  "layout": "downloads",
  "description": "Revenue collected from extraction of natural resources on federal and Native American lands that is distributed to various legislated funds, local governments, and federal agencies. This process is called disbursement.",
  "tags": ["Data", "Downloads", "Documentation", "Disbursements"]
};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const DownloadDataFile = makeShortcode("DownloadDataFile");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><a parentName="p" {...{
        "href": "/sites/2702-revenue-type-list/downloads/"
      }}>{`Downloads /`}</a></p>
    <h1 {...{
      "id": "Disbursements",
      "style": {
        "position": "relative"
      }
    }}>{`Disbursements`}</h1>
    <p>{`Revenue collected from extraction of natural resources on federal and Native American lands that is distributed to various legislated funds, local governments, and federal agencies. This process is called disbursement. We have disbursements data by fiscal year from 2003 to present.`}</p>
    <br />
    <p>{`Download fiscal year data:`}</p>
    <DownloadDataFile linkType="DownloadCsv" dataSet="fiscal_year_disbursements.csv" mdxType="DownloadDataFile">
  Fiscal year disbursements
    </DownloadDataFile>
    <DownloadDataFile linkType="DownloadXls" dataSet="fiscal_year_disbursements.xlsx" mdxType="DownloadDataFile">
  Fiscal year disbursements
    </DownloadDataFile>
    <h2 {...{
      "id": "Scope",
      "style": {
        "position": "relative"
      }
    }}>{`Scope`}</h2>
    <p>{`This dataset includes natural resource disbursements for U.S. federal lands, federal offshore areas, and Native American lands. It does not include privately owned lands or U.S. state lands. The dataset is tracked and managed by the Department of the Interior’s Office of Natural Resources Revenue. It contains disbursement information to funds for fiscal years 2003-2023. Disbursements of revenue from extractive activities on U.S. federal lands occur monthly. This dataset is a sum of those disbursements by fiscal year.`}</p>
    <h2 {...{
      "id": "Data-publication",
      "style": {
        "position": "relative"
      }
    }}>{`Data publication`}</h2>
    <p>{`The disbursement dataset is updated in November after the end of the federal government fiscal year.`}</p>
    <h2 {...{
      "id": "Data-dictionary",
      "style": {
        "position": "relative"
      }
    }}>{`Data dictionary`}</h2>
    <h3 {...{
      "id": "Fields-and-Definitions",
      "style": {
        "position": "relative"
      }
    }}>{`Fields and Definitions`}</h3>
    <p>{`Laws treat revenues from offshore natural resources and onshore natural resources differently. There are set percentages and amounts from each that go to specific funds, local governments, or government agencies. All revenue collected from Native American lands is disbursed back to Native American tribes, nations, or individuals.`}</p>
    <br />
    <p><strong parentName="p">{`Fiscal Year:`}</strong>{`  The year the disbursement occurred. The federal fiscal year runs from October 1 of the prior year through September 30 of the year being described. For example, Fiscal Year 2018 is between October 1, 2017,  and September 30, 2018.`}</p>
    <br />
    <p><strong parentName="p">{`Fund Type:`}</strong>{`  The type of recipient receiving the disbursement.`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Historic Preservation Fund:`}</strong>{`  This fund helps preserve U.S. historical and archaeological sites and cultural heritage. This fund supports grants to state and tribal historic preservation offices.`}</li>
      <li parentName="ul"><strong parentName="li">{`Land and Water Conservation Fund (LWCF):`}</strong>{` This fund provides matching grants to states and local governments to buy and develop public outdoor recreation areas across the 50 states. The Gulf of Mexico Energy Security Act (GOMESA) of 2006 specifies that 12.5% of revenues from certain gulf leases be directed to the LWCF stateside program. States can receive up to $125 million a year in funding. This portion of LWCF funds is not subject to the congressional appropriation process. Otherwise, they are treated similarly to regular LWCF funds.`}</li>
      <li parentName="ul"><strong parentName="li">{`Native American Tribes and Individuals:`}</strong>{`  ONRR disburses 100% of revenue collected from resource extraction on Native American lands back to Native American tribes, nations, and individuals.`}</li>
      <li parentName="ul"><strong parentName="li">{`Other:`}</strong>{`  Certain funds are directed back to the federal agencies that administer these lands to help cover the agencies’ operational costs. Agencies that receive funds include: BLM, U.S. Fish and Wildlife Service, and U.S. Forest Service. The Ultra-Deepwater Research Program and the Mescal Settlement Agreement also receive $50 million each.`}</li>
      <li parentName="ul"><strong parentName="li">{`Reclamation Fund:`}</strong>{`  Established by Congress in 1902 to pay for Bureau of Reclamation projects. This fund supports the establishment of critical infrastructure projects like dams and power plants.`}</li>
      <li parentName="ul"><strong parentName="li">{` State and Local Governments:`}</strong>{` Funds disbursed to states fall under the jurisdiction of each state. Each state determines how the funds will be used.`}</li>
      <li parentName="ul"><strong parentName="li">{`U.S. Treasury:`}</strong>{`  Funds disbursed to the Treasury go to the General Fund, which is the federal government’s basic operating fund. The General Fund pays for roughly two-thirds of all federal expenditures, including the U.S. military, national parks, and schools.`}</li>
    </ul>
    <p><strong parentName="p">{`Source:`}</strong>{`  Whether the origin of the revenue being disbursed is offshore or onshore`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Onshore:`}</strong>{`  Situated or occurring on land.`}</li>
      <li parentName="ul"><strong parentName="li">{`Offshore:`}</strong>{`  Submerged lands located farther than three miles off a state’s coastline, or three marine leagues into the Gulf of Mexico off Texas and western Florida`}</li>
      <li parentName="ul"><strong parentName="li">{`GOMESA-Offshore:`}</strong>{` The Gulf of Mexico Energy Security Act (GOMESA) of 2006 specifies that 50% of revenues from certain gulf leases be directed to the U.S. Treasury. 37.5% of revenue from certain leases in the Gulf of Mexico are shared with Alabama, Louisiana, Mississippi, and Texas.`}</li>
      <li parentName="ul"><strong parentName="li">{`8(g) Offshore:`}</strong>{` 27% of revenue from leases in the 8(g) Zone are shared with states. The 8(g) Zone is the first three nautical miles of the Outer Continental Shelf.`}<br /></li>
    </ul>
    <p><strong parentName="p">{`State:`}</strong>{`  The state that received the disbursement in accordance with revenue-sharing legislation.`}</p>
    <br />
    <p><strong parentName="p">{`County:`}</strong>{`  The county that received the disbursement in accordance with revenue-sharing legislation.`}</p>
    <br />
    <p><strong parentName="p">{`Disbursement:`}</strong>{`  The amount disbursed.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      